import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Stethoscope, Bike, FileText, Image,
  Download, Clock, AlertCircle, CheckCircle, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useFetch } from '../hooks';
import { supportTicketService } from '../services/api';
import { TicketStatusBadge, TICKET_STATUS_CONFIG } from './SupportTickets';
import { formatDate } from '../utils/helpers';

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-start gap-4 py-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
    <span className="text-sm font-medium text-gray-900 dark:text-white text-right">{value || '—'}</span>
  </div>
);

const Section = ({ icon: Icon, title, iconColor = 'text-medihub-primary', children }) => (
  <div className="card p-5">
    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
      <Icon className={`w-4 h-4 ${iconColor}`} />{title}
    </h3>
    {children}
  </div>
);

const userTypeIcon = (type) => {
  const t = type?.toLowerCase();
  if (t === 'doctor')   return <Stethoscope className="w-5 h-5 text-blue-500" />;
  if (t === 'delivery') return <Bike className="w-5 h-5 text-orange-500" />;
  return <User className="w-5 h-5 text-purple-500" />;
};

const userTypeCls = {
  patient:  'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400',
  doctor:   'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
  delivery: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400',
};

/* ── Resolve Section ────────────────────────────────────────────────────────── */
const ResolveSection = ({ ticket, onSuccess }) => {
  const [status,    setStatus]    = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [loading,   setLoading]   = useState(false);

  const isResolved = ticket.status === 'resolved' || ticket.status === 'rejected';

  const handleSubmit = async () => {
    if (!status)           { toast.error('Select an action'); return; }
    if (!adminNote.trim()) { toast.error('Admin note is required'); return; }
    setLoading(true);
    try {
      const res = await supportTicketService.resolve(ticket.id, status, adminNote.trim());
      toast.success(res?.message || `Ticket ${status}`);
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Section icon={AlertCircle} title="Resolve Ticket" iconColor="text-yellow-500">
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
          <span className="text-xs text-gray-500">Current:</span>
          <TicketStatusBadge status={ticket.status} />
        </div>

        {isResolved ? (
          <div className="p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl space-y-1">
            <p className="text-xs text-gray-500">Admin Note</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{ticket.admin_note || '—'}</p>
            {ticket.resolved_at && (
              <p className="text-xs text-gray-400">Resolved: {formatDate(ticket.resolved_at?.split('T')[0])}</p>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setStatus('resolved')}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  status === 'resolved'
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-green-300 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}>
                <CheckCircle className="w-4 h-4" />Resolve
              </button>
              <button type="button" onClick={() => setStatus('rejected')}
                className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  status === 'rejected'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                }`}>
                <XCircle className="w-4 h-4" />Reject
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Admin Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={adminNote}
                onChange={e => setAdminNote(e.target.value)}
                className="input-field resize-none"
                rows={3}
                placeholder={status === 'resolved' ? 'e.g. Issue has been fixed' : status === 'rejected' ? 'e.g. Invalid request' : 'Write admin note...'}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={loading || !status || !adminNote.trim()}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? 'Submitting…' : `Submit${status ? ` (${status})` : ''}`}
            </button>
          </>
        )}
      </div>
    </Section>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const SupportTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: ticket, loading, refetch } = useFetch(() => supportTicketService.getById(id), [id]);

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1,2,3].map(i => <div key={i} className="card p-5 h-48 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
      </div>
    </div>
  );

  if (!ticket) return (
    <div className="text-center py-20">
      <p className="text-gray-500">Ticket not found.</p>
      <button onClick={() => navigate('/support')} className="btn-primary mt-4">Back to Support</button>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/support')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">#{ticket.id}</h1>
            <TicketStatusBadge status={ticket.status} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Created {formatDate(ticket.created_at?.split('T')[0])}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left */}
        <div className="lg:col-span-2 space-y-5">

          {/* User Info */}
          <Section icon={User} title="User Information">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 rounded-xl overflow-hidden shrink-0 ${userTypeCls[ticket.user_type?.toLowerCase()] || 'bg-gray-100'}`}>
                {ticket.user_img
                  ? <img src={ticket.user_img} alt={ticket.user_name} className="w-full h-full object-cover" />
                  : userTypeIcon(ticket.user_type)
                }
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{ticket.user_name}</p>
                <p className="text-xs text-gray-500 capitalize">{ticket.user_type}</p>
              </div>
            </div>
            <InfoRow label="Mobile"    value={ticket.user_mobile} />
            <InfoRow label="User Type" value={<span className="capitalize">{ticket.user_type}</span>} />
          </Section>

          {/* Issue Details */}
          <Section icon={FileText} title="Issue Details" iconColor="text-blue-500">
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="font-semibold text-gray-900 dark:text-white">{ticket.title}</p>
              <p className="text-xs text-gray-500 mt-1">{formatDate(ticket.created_at?.split('T')[0])}</p>
            </div>
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{ticket.description}</p>
            </div>
            {ticket.image_url && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Attachment</p>
                <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl shrink-0">
                    <Image className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ticket.image_url}</p>
                  </div>
                  <a href={ticket.image_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors shrink-0">
                    <Download className="w-3.5 h-3.5" />View
                  </a>
                </div>
              </div>
            )}
          </Section>
        </div>

        {/* Right */}
        <div className="space-y-5">
          {/* Resolve */}
          <ResolveSection ticket={ticket} onSuccess={refetch} />

          {/* Ticket Summary */}
          <div className="card p-5 space-y-1">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />Ticket Summary
            </h3>
            <InfoRow label="Ticket ID"   value={<span className="font-mono text-xs">#{ticket.id}</span>} />
            <InfoRow label="Status"      value={<TicketStatusBadge status={ticket.status} />} />
            <InfoRow label="Admin Note"  value={ticket.admin_note} />
            <InfoRow label="Created"     value={formatDate(ticket.created_at?.split('T')[0])} />
            <InfoRow label="Resolved At" value={ticket.resolved_at ? formatDate(ticket.resolved_at?.split('T')[0]) : '—'} />
          </div>

          {/* Status Guide */}
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-gray-400" />Status Guide
            </h3>
            <div className="space-y-2">
              {Object.entries(TICKET_STATUS_CONFIG).map(([key, { label, cls, icon: Icon }]) => (
                <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${ticket.status === key ? cls + ' font-semibold' : 'opacity-40'}`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-xs">{label}</span>
                  {ticket.status === key && <span className="ml-auto text-xs">← Current</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetail;
